// HierarchyWorkspace — shared "left TreeNav + draggable splitter + Breadcrumb + drill-down DataGrid" shell.
//
// Presentational layout ONLY. Owns: the flex layout, the left nav width + drag splitter +
// localStorage persistence, the <main> wrapper, and an optional right-panel slot.
// Composes the existing TreeNav / Breadcrumb / DataGrid (Tier-1 primitives) — it does NOT
// fetch data, know any domain, or own any data state. Everything data-related stays in the
// parent screen (Catalog in App.jsx, Products in ProductsView.jsx, Functions later).
//
// Two composition modes (a consumer uses exactly one):
//   • Structured mode — pass breadcrumbs + gridRows + gridColumns + gridHandlers + emptyState;
//     the shell renders <Breadcrumb> then a single <DataGrid> for you (the simple drill-down case,
//     e.g. the future Functions screen).
//   • Slot mode — pass mainHeader (the full breadcrumb/toolbar row) and children (the main body);
//     the shell renders them verbatim inside <main>. Used by Catalog and Products, whose header
//     rows carry screen-specific extras and whose bodies are richer than one grid.
//
// Props:
//   treeNodes         — TreeNode[] for TreeNav
//   selectedNodeId    — string | null
//   onSelectNode      — (id: string | null) => void
//   treeTitle?        — TreeNav title header (selects root on click)
//   treeHeader?       — node rendered ABOVE TreeNav, inside the same aside (NIGHT_SHIFT
//                       item 14, 2026-07-23) — the view-switcher slot. Suppressed
//                       whenever `hideNav` is set, same as the tree itself, so an
//                       embedded screen (Onshape's Catalog/Products embeds) never
//                       sprouts a second switcher.
//   filterPlaceholder?— TreeNav filter placeholder
//   noTree?           — keep the left rail (treeHeader + splitter, so it still resizes
//                       and hides) but draw no TreeNav under it: a screen with nothing
//                       to put in a tree yet, e.g. the project list (Alex, 2026-10-20:
//                       on Standard, Functions lost the adjustable left pane).
//   treeFilterAside?  — TreeNav `filterAside`: a control on the filter's own row
//   treeSideIcons?    — passthrough to TreeNav's `sideIcons` (NIGHT_SHIFT item 7b/task 3,
//                       2026-07-21) — the Onshape screen's composite tree is the only
//                       caller that sets this; default false leaves Catalog/Products untouched.
//   storageKey        — namespace; default DataGrid storageKey in structured mode, and the
//                       fallback nav-width key ("inoviu:<storageKey>NavWidth")
//   navWidthStorageKey?— explicit localStorage key for the nav width (verbatim). Use this to
//                       preserve a pre-existing key that doesn't match the derived pattern
//                       (Catalog: "inoviu:navWidth"). Defaults to "inoviu:<storageKey>NavWidth".
//   defaultNavWidth?  — initial nav width when nothing is persisted (default 260)
//   navMin?, navMax?  — clamp bounds for the nav width (default 160 / 520)
//   (no prop) — navWidth and rightWidth are also clamped so their SUM never exceeds
//   70% of the window width, so <main> always keeps >=30% on a narrow window instead
//   of the two drag-set side columns holding their pixel width while <main> absorbs
//   all the shrinkage (Alex, 2026-08-21). Display-only — the persisted/dragged value
//   is untouched; widening the window snaps straight back to it.
//   mainStyle?        — style overrides merged onto the <main> element
//   rightPanel?       — node rendered as a sibling of <main> (e.g. ParameterPanel); omit to hide.
//                       The workspace OWNS the resizable width for this slot (mirrors the left
//                       nav's drag-resize) — the panel itself should NOT set its own fixed width
//                       (it fills the slot; see ParameterPanel / OnshapeView's detail panel).
//   rightPanelWidthStorageKey?— explicit localStorage key for the right-panel width (verbatim).
//                       Defaults to "inoviu:<storageKey>RightWidth".
//   defaultRightWidth?— initial right-panel width when nothing is persisted (default 280)
//   rightMin?, rightMax?— clamp bounds for the right-panel width (default 200 / 480)
//   rightBottom?      — node for the right panel's LOWER region (Alex's sketch, 2026-08-13:
//                       "specific right panel" above, "chat panel" below, movable divider).
//                       A SLOT, never an import — both customer screens use this shell and
//                       check-customer-view's import walk is what keeps the assistant off
//                       them. The workspace owns the frame, the title and the collapse; the
//                       node passed in is CONTENT and styles nothing (UI_VISION §3).
//   rightBottomTitle? — the lower region's SectionTitle text (default 'Assistant')
//   defaultBottomHeight?— initial lower-region height when nothing is persisted (default 280)
//   bottomMin?, bottomMax?— clamp bounds for the lower region (default 120 / 720)
//   // -- slot mode --
//   mainHeader?       — node rendered at the top of <main> (breadcrumb/toolbar row)
//   children?         — main body
//   // -- structured mode --
//   breadcrumbs?      — crumbs[] for <Breadcrumb>; renders nothing when empty
//   gridRows?, gridColumns? — passed to the composed <DataGrid>
//   gridHandlers?     — passthrough bag spread onto <DataGrid> (onRowClick / onRowDoubleClick /
//                       onSave / onAdd / onDelete / onReorder / selectedRowId / editRowId /
//                       renderSubRow / loading / footerRow / …)
//   emptyState?       — DataGrid emptyMessage
//
// Styling: tokens only (--th-* / --tg-*). No raw fontSize / monospace / hex.

import { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import TreeNav from './TreeNav.jsx';
import Breadcrumb from './Breadcrumb.jsx';
import DataGrid from './DataGrid.jsx';
import SectionTitle from './SectionTitle.jsx';
import Card from './Card.jsx';
import { CARD_STYLE, PANE_GAP } from '../theme-tokens.js';
import { useAuthGroup } from '../AuthGate.jsx';
import { DISCLOSURE } from '../icons.js';

// SplitterGrip — the collapse control for the left tree / right panel, sitting
// ON the splitter bar itself instead of pinned to a top corner (Alex, 2026-08-27:
// "I did not see the chevron on the top... can the chevron be in the middle of
// the separation bar... 3 chevrons vertically"). Three stacked DISCLOSURE glyphs
// read as a grip — the same idea as the row-reorder grip (⠿) elsewhere in the
// app, just this shape for this job — rather than one easy-to-miss arrow.
// `flip` points the stack the other way (180°) for the mirrored side.
// `onMouseDown` stops propagation so a click here never starts a drag on the
// splitter div it sits inside.
function SplitterGrip({ onClick, title, flip = false, expanded }) {
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseDown={e => e.stopPropagation()}
      title={title}
      aria-expanded={expanded}
      style={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
        background: 'var(--th-row-even)', border: '1px solid var(--th-border-subtle)', borderRadius: 3,
        padding: '4px 1px', cursor: 'pointer', color: 'var(--th-text-secondary)', lineHeight: 1,
      }}
    >
      {[0, 1, 2].map(i => (
        <span
          key={i} aria-hidden="true"
          style={{ fontSize: 'var(--fs-body)', display: 'inline-block', transform: flip ? 'rotate(180deg)' : 'none' }}
        >{DISCLOSURE}</span>
      ))}
    </button>
  );
}

// ⚠ THE PARKING SPACES BELONG TO WHOEVER GOT HERE FIRST. Alex, 2026-08-16, on seeing two
// chat panels: "This should be impossible!" — and asking for "three parking spaces, left,
// middle and right", each with its spots.
//
// It was possible because a screen does not merely FILL this layout, it CONSTRUCTS one: four
// screens each render their own HierarchyWorkspace. That is fine until one screen renders
// another (OnshapeView embeds CatalogView/ProductsView as its Main), and then two layouts
// exist and neither can see the other. Gating the guest on `hideNav` fixed the symptom and
// left the cause: the guest still declared regions and merely chose not to fill one.
//
// So the layout now knows whether it is nested. A workspace inside another workspace
// renders ONLY the middle — no tree, no right column, no assistant — whatever props it was
// given. Not "should not": CANNOT, because the regions are behind this check. The host owns
// the parking; a guest parks in the middle.
//
// Context rather than a prop, deliberately: a prop has to be threaded correctly by every
// caller forever, which is the same discipline that failed. Nesting is a fact about the
// tree, so the tree is what should report it.
const NestedWorkspace = createContext(false);

// ONE PARKING SPOT IN THE MIDDLE. The shell owns the chrome — title bar, collapse, and
// remembering whether you left it open — so a screen contributes CONTENT and nothing else.
// Alex's rule from the 08-13 sketch: "content does not own its chrome, REACT should take
// care of that." Before this, every screen wired its own SectionTitle and none of them
// remembered anything, so the same block collapsed differently depending which screen you
// were on.
//
// ⚠ A spot with NO title renders bare, and that is deliberate rather than an omission.
// OnshapeSection and WhereUsedPanel are shared components that already draw their own
// collapsible frame on six surfaces; wrapping them would give them two title bars. They
// become titled spots the day they stop drawing their own, not before.
//
// ⚠ One scroll for the whole middle (Alex, 2026-08-16), so a spot never gets its own
// scrollbar. Blocks collapse; they do not resize. The <main> above is the only scroller.
function Spot({ spot, storageKey }) {
  // Hooks called unconditionally every render, regardless of which branch below
  // ends up used — `open`/`toggle` are real state ONLY the non-card-with-title
  // path (the last return) reads; the card-with-title path delegates its own
  // open state to <Card>, and card-without-title / bare paths don't collapse
  // at all. Kept unconditional on purpose: a given spot's card/title shape is
  // stable for its own key across a session in practice, but branching a hook
  // call itself on that shape is the fragile version of that fact, not a safe
  // shortcut — see ProductsView's SearchResultsPopover extraction for the same
  // reasoning applied the other direction.
  const openKey = `inoviu:${storageKey}:spot:${spot.key}`;
  const [open, setOpen] = useState(() => {
    const saved = localStorage.getItem(openKey);
    return saved === null ? spot.defaultOpen !== false : saved !== '0';
  });

  const toggle = useCallback(() => {
    setOpen((o) => {
      const next = !o;
      localStorage.setItem(openKey, next ? '1' : '0');
      return next;
    });
  }, [openKey]);

  // card + title — delegates to the standalone Card primitive (2026-08-20,
  // rolling the card pattern past the spots-only mechanism). Same open-key
  // SHAPE as before this delegation (`${storageKey}:spot:${spot.key}`, just
  // passed through Card's own `inoviu:${storageKey}` prefix) so nobody's
  // already-persisted collapse state resets under a renamed key.
  if (spot.title && spot.card) {
    return (
      <Card
        storageKey={`${storageKey}:spot:${spot.key}`}
        title={spot.title}
        count={spot.count}
        right={spot.right}
        icon={spot.icon}
        defaultOpen={spot.defaultOpen !== false}
        onOpen={spot.onOpen}
      >
        {spot.content}
      </Card>
    );
  }

  // card, no title — the spot's own content already draws its own title
  // (Configuration, Not synced) — this card only adds the box around it. No
  // shell-level collapse for these; that stays exactly as it was before the
  // card pass.
  //
  // ⚠ Only catches spot.content passed as a LITERAL null/undefined — it cannot
  // see through an unevaluated `<SomeComponent/>` element to know that
  // component's own render will return null (found 2026-08-20 as "a loose
  // rectangle between Where Used and Components": SyncWorklistSection returns
  // null for a product with no onshape_ref, but Spot still drew a border
  // around the element descriptor, which is truthy regardless). The real fix
  // for that case is gating the SPOT OUT of productSpots entirely at the call
  // site — see ProductsView.jsx's `sync-worklist` spot guard.
  if (!spot.title) {
    if (!spot.card || !spot.content) return spot.content ?? null;
    return <div style={CARD_STYLE}>{spot.content}</div>;
  }

  const titleRow = (
    <SectionTitle collapsed={!open} onToggle={toggle} count={spot.count} right={spot.right}>
      {spot.title}
    </SectionTitle>
  );

  return (
    <div style={{ marginBottom: 10 }}>
      {titleRow}
      {open && spot.content}
    </div>
  );
}

export default function HierarchyWorkspace({
  treeNodes,
  selectedNodeId,
  onSelectNode,
  treeTitle,
  treeHeader,
  filterPlaceholder,
  treeFilterAside = null,
  noTree = false,
  treeSideIcons = false,
  // Passed straight to TreeNav: an EMPTY tree is not a loading tree (see TreeNav).
  treeLoading,
  treeEmptyMessage,
  // hideNav — render WITHOUT the left tree + splitter (2026-07-20): the Onshape
  // screen embeds a whole screen (CatalogView/ProductsView) as its Main, and the
  // embedded screen must not bring a second tree. A flag on the one shell, not
  // a second shell (UI_VISION §4).
  hideNav = false,
  storageKey,
  navWidthStorageKey,
  defaultNavWidth = 260,
  navMin = 160,
  navMax = 520,
  mainStyle,
  rightPanel,
  rightBottom,
  // rightSelected — gates the drag splitter for the right column, same reason
  // and same shape as `selectedNodeId` gating the left one (Alex, 2026-08-20:
  // "only show up when selection is there"). Default true: rightPanel is often
  // reserved-but-empty by design (the chat-stays-pinned rule below), and most
  // callers have no finer-grained "selected" concept than that — a screen with
  // one (Projects: a placement) passes it explicitly.
  rightSelected = true,
  // ⚠ THE NAME SAYS WHO IT IS FOR. Alex, 2026-08-26: 'we also need to change the
  // name stating "AI assistant" and, in parentheses, say "internal only"'. A
  // greyed card explains itself only on hover; the title explains itself always,
  // and every one of the four callers takes this default.
  rightBottomTitle = 'AI Assistant (internal only)',
  defaultBottomHeight = 280,
  bottomMin = 120,
  bottomMax = 720,
  rightPanelWidthStorageKey,
  defaultRightWidth = 280,
  rightMin = 200,
  rightMax = 480,
  // slot mode
  mainHeader,
  // spots — the MIDDLE's ordered list of blocks (Alex's parking model, 2026-08-16:
  // "three parking spaces… each having parking spot, one or two or growing vertically
  // for the middle one"). `mainHeader` is the middle's one NAMED spot — the title row,
  // which is "nothing more than the detail row from the level above" (2026-08-14) — and
  // everything below it is this list, which grows.
  //
  // ⚠ THE MIDDLE IS THREE THINGS, IN THIS ORDER: mainHeader (the title row), `children`
  // (the level's OWN content — its grid, its forms, its notices) and then `spots` (the
  // section stack). `children` is not legacy: a category page is a grid and nothing else,
  // and wrapping a grid in a titled collapsible block would be chrome for its own sake.
  //
  // Each entry: { key, title?, count?, right?, content, defaultOpen? }
  //   title   — given, the SHELL draws the title bar, the collapse and the remembering.
  //             Omitted, the block renders bare: for content that owns its own chrome
  //             (OnshapeSection, WhereUsedPanel) or has none (a grid).
  //   key     — identity for persistence; must be stable, and is namespaced by storageKey.
  // Falsy entries are dropped, so a screen can write `cond && {…}` inline.
  spots = [],
  children,
  // structured mode
  breadcrumbs,
  gridRows,
  gridColumns,
  gridHandlers,
  emptyState,
}) {
  // Nested => the host already owns every region. `hideNav` stays honoured for the callers
  // that set it explicitly, but nesting alone is now enough, and it is the half that cannot
  // be forgotten.
  const nested = useContext(NestedWorkspace);
  const ownsRegions = !nested;
  const showNav = ownsRegions && !hideNav;

  // Viewport width, for the responsive side-column clamp below (Alex, 2026-08-21:
  // "when I go from a large screen to a small screen, the middle shrinks a lot
  // versus the right and the left, which doesn't change"). navWidth/rightWidth
  // are drag-set pixel values with no idea how wide the window actually is —
  // on a narrow window they can eat the whole thing, leaving nothing for <main>.
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const navWidthKey = navWidthStorageKey || `inoviu:${storageKey}NavWidth`;

  const [navWidth, setNavWidth] = useState(() => {
    const saved = localStorage.getItem(navWidthKey);
    return saved ? Math.max(navMin, Math.min(navMax, Number(saved))) : defaultNavWidth;
  });
  const navWidthRef = useRef(navWidth);
  const dragState = useRef({ active: false, startX: 0, startW: 0 });

  const onSplitterMouseDown = useCallback((e) => {
    dragState.current = { active: true, startX: e.clientX, startW: navWidthRef.current };
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    e.preventDefault();
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragState.current.active) return;
      const w = Math.max(navMin, Math.min(navMax, dragState.current.startW + e.clientX - dragState.current.startX));
      navWidthRef.current = w;
      setNavWidth(w);
    };
    const onUp = () => {
      if (!dragState.current.active) return;
      dragState.current.active = false;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      localStorage.setItem(navWidthKey, String(navWidthRef.current));
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [navMin, navMax, navWidthKey]);

  // ── HIDE THE LEFT NAV ────────────────────────────────────────────────────
  // Mirrors the right panel's hide, below, added for consistency (Alex: "I will
  // need the right side to be able to collapse... better to do for both as
  // consistency" — the right one shipped first, 2026-08-27, for the same laptop-
  // real-estate reason). Same shape throughout: hidden not narrowed, collapses to
  // a 22px rail carrying only the control that brings it back, persisted per
  // storageKey so hiding the tree on Products does not hide it on Catalog.
  const navHiddenKey = `inoviu:${storageKey}NavHidden`;
  const [navHidden, setNavHidden] = useState(() => {
    try { return localStorage.getItem(navHiddenKey) === '1'; } catch { return false; }
  });
  const toggleNavHidden = useCallback(() => {
    setNavHidden((h) => {
      const next = !h;
      try { localStorage.setItem(navHiddenKey, next ? '1' : '0'); } catch { /* private mode */ }
      return next;
    });
  }, [navHiddenKey]);

  // Right panel — same drag-resize pattern as the left nav, mirrored (splitter
  // sits on the panel's LEFT edge; dragging it left grows the panel since the
  // panel is anchored to the right edge of the workspace).
  const rightWidthKey = rightPanelWidthStorageKey || `inoviu:${storageKey}RightWidth`;

  const [rightWidth, setRightWidth] = useState(() => {
    const saved = localStorage.getItem(rightWidthKey);
    return saved ? Math.max(rightMin, Math.min(rightMax, Number(saved))) : defaultRightWidth;
  });
  const rightWidthRef = useRef(rightWidth);
  const rightDragState = useRef({ active: false, startX: 0, startW: 0 });

  // ── HIDE THE RIGHT COLUMN ────────────────────────────────────────────────
  // Alex, 2026-08-27: "Is there an easy way to hide the right pan. Working on a
  // laptop for the next two weeks and it will help w/ real-estate."
  //
  // ⚠ HIDDEN, NOT NARROWED. Dragging it to `rightMin` still leaves 200px plus a
  // splitter, which is most of what a 1280px laptop cannot spare. Collapsed, the
  // column becomes a 22px rail carrying only the control that brings it back —
  // so the space genuinely returns to <main>, and the panel never becomes
  // unreachable (a hidden region with no visible way back is how a feature gets
  // reported missing).
  //
  // Persisted per storageKey, so hiding it on Products does not hide it on
  // Catalog — the screens have different right panels and different reasons to
  // want one.
  const rightHiddenKey = `inoviu:${storageKey}RightHidden`;
  const [rightHidden, setRightHidden] = useState(() => {
    try { return localStorage.getItem(rightHiddenKey) === '1'; } catch { return false; }
  });
  const toggleRightHidden = useCallback(() => {
    setRightHidden((h) => {
      const next = !h;
      try { localStorage.setItem(rightHiddenKey, next ? '1' : '0'); } catch { /* private mode */ }
      return next;
    });
  }, [rightHiddenKey]);

  const onRightSplitterMouseDown = useCallback((e) => {
    rightDragState.current = { active: true, startX: e.clientX, startW: rightWidthRef.current };
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    e.preventDefault();
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!rightDragState.current.active) return;
      const w = Math.max(rightMin, Math.min(rightMax, rightDragState.current.startW + rightDragState.current.startX - e.clientX));
      rightWidthRef.current = w;
      setRightWidth(w);
    };
    const onUp = () => {
      if (!rightDragState.current.active) return;
      rightDragState.current.active = false;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      localStorage.setItem(rightWidthKey, String(rightWidthRef.current));
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [rightMin, rightMax, rightWidthKey]);

  // Responsive clamp (Alex, 2026-08-21) — never let navWidth + rightWidth eat more
  // than 70% of the viewport, so <main> keeps at least 30% on a narrow window
  // instead of absorbing 100% of the shrinkage while the two drag-set side columns
  // stay put. DISPLAY-ONLY: scales what's rendered, not navWidth/rightWidth
  // themselves or their localStorage entries — a drag still sets/persists the
  // real preference, and widening the window back out snaps straight to the full
  // dragged width with nothing to restore. Not a redesign of the drag mechanism,
  // just a ceiling on top of it.
  // ⚠ THE ASSISTANT IS INTERNAL-ONLY, AND THIS IS THE POLITE HALF OF THAT.
  // The six /api/assistant routes are already 'internal' in route-policy.js, so a
  // customer or project login is refused server-side whatever this renders. What
  // this adds is not stopping them — it is not offering. Alex, 2026-08-26: 'for
  // the other group... I would like to see that card close gray out.'
  //
  // applicable=false is the established contract (UI_VISION §4, and Card's own
  // applicable/naReason pair): AVAILABILITY is a fact, open/closed is a
  // preference, and absent is neither. So the card keeps its place and its name,
  // greys, stays shut, and says why on hover — rather than vanishing, which would
  // leave a person wondering whether the feature exists at all.
  //
  // This layout component knowing WHICH slot is internal-only is a small
  // exception to rightBottom being an opaque slot, taken because the alternative
  // is the same three lines in four callers — and four copies of a permission
  // rule is how three of them end up out of date.
  const authGroup = useAuthGroup();
  // ⚠ AND ON THE DEMO IT IS UNAVAILABLE TO EVERYONE, INTERNAL INCLUDED. The web
  // instance ships with no ANTHROPIC_API_KEY on purpose — that is what makes the
  // spend physically unreachable rather than merely unused — so assistant.js
  // throws the moment anyone asks it anything. Offering a card that can only
  // produce an error is worse than not offering it: on a site whose whole job is
  // to make a good first impression, it reads as broken software rather than as
  // a deliberately absent key.
  const isDemo = typeof window !== 'undefined' && !!window.__INOVIU_DEMO__;
  const bottomNa = isDemo || authGroup !== 'internal';
  const bottomNaReason = isDemo
    ? 'The AI assistant is turned off on the demo site — it is not connected to a language model here.'
    : 'The AI assistant is internal-only. It reads the whole cost catalog, so it is not available to customer or single-project logins.';

  // showNav still means "this region applies at all" (used to gate the JSX
  // block below, collapsed rail included) — navContentVisible narrows that to
  // "the full tree is what's showing", which is what the width cap and clamp
  // below actually care about: a collapsed rail costs 22px, not navWidth.
  const navContentVisible = showNav && !navHidden;
  const showRight = ownsRegions && !!(rightPanel || rightBottom) && !rightHidden;
  const sideCap = viewportWidth * 0.7;
  const rawSideTotal = (navContentVisible ? navWidth : 0) + (showRight ? rightWidth : 0);
  const sideScale = rawSideTotal > sideCap && rawSideTotal > 0 ? sideCap / rawSideTotal : 1;
  const effectiveNavWidth = navContentVisible ? navWidth * sideScale : navWidth;
  const effectiveRightWidth = showRight ? rightWidth * sideScale : rightWidth;

  // --- the right panel's SECOND region: a horizontal split (Alex's sketch, 2026-08-13)
  // "Specific right panel" on top, "Chat Panel — can open & close like Onshape" below,
  // with a movable divider "like the left panel". Same drag shape as the two widths
  // above, turned 90°: dragging UP grows the bottom, which is why the delta is
  // startY - clientY rather than the other way round.
  //
  // ⚠ THE WORKSPACE OWNS THE FRAME AND THE COLLAPSE, THE SLOT IS ONLY CONTENT.
  // Alex's rule (UI_VISION §3, "content does not own its chrome"): the container
  // renders the SectionTitle and the chevron; whatever is passed in fills the space and
  // styles nothing. That is also why `rightBottom` is a SLOT and never an import — both
  // customer screens use this shell, and check-customer-view's import walk is the only
  // thing keeping the assistant off the customer side.
  const bottomHeightKey = `inoviu:${storageKey}RightBottomHeight`;
  const bottomOpenKey = `inoviu:${storageKey}RightBottomOpen`;
  const [bottomHeight, setBottomHeight] = useState(() => {
    const saved = localStorage.getItem(bottomHeightKey);
    return saved ? Math.max(bottomMin, Math.min(bottomMax, Number(saved))) : defaultBottomHeight;
  });
  const [bottomOpen, setBottomOpen] = useState(() => localStorage.getItem(bottomOpenKey) !== '0');
  const bottomHeightRef = useRef(bottomHeight);
  bottomHeightRef.current = bottomHeight;
  const bottomDragState = useRef({ active: false, startY: 0, startH: 0 });

  const onBottomSplitterMouseDown = useCallback((e) => {
    bottomDragState.current = { active: true, startY: e.clientY, startH: bottomHeightRef.current };
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'row-resize';
    e.preventDefault();
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!bottomDragState.current.active) return;
      const h = Math.max(bottomMin, Math.min(bottomMax,
        bottomDragState.current.startH + bottomDragState.current.startY - e.clientY));
      setBottomHeight(h);
    };
    const onUp = () => {
      if (!bottomDragState.current.active) return;
      bottomDragState.current.active = false;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      localStorage.setItem(bottomHeightKey, String(bottomHeightRef.current));
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [bottomMin, bottomMax, bottomHeightKey]);

  const toggleBottom = useCallback(() => {
    setBottomOpen((o) => {
      localStorage.setItem(bottomOpenKey, o ? '0' : '1');
      return !o;
    });
  }, [bottomOpenKey]);

  const useSlotMode = mainHeader !== undefined || children !== undefined;

  const mergedMainStyle = {
    flex: 1, overflow: 'auto', padding: PANE_GAP,
    ...mainStyle,
  };

  // Pinned-header middle section (Alex, 2026-08-21: "is it possible to freeze
  // the header card?"… "Same for all for the middle section. Typical" — built
  // in here rather than hand-rolled per screen, which is how Catalog's own
  // version started). A caller opts in by giving `mainStyle` overflow:'hidden'
  // (already what a caller does to take manual control of <main>'s layout —
  // Catalog was the first) — that SAME signal now also means "keep mainHeader
  // OUTSIDE the scroll, and give children+spots ONE shared scroll region
  // below it", instead of every screen wrapping its own children in an
  // identical div. A caller that leaves the default overflow:'auto' is
  // untouched — mainHeader/children/spots still flow as one block, byte-
  // identical to before this existed.
  const pinnedHeader = mergedMainStyle.overflow === 'hidden';

  return (
    <NestedWorkspace.Provider value>
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      {showNav && (navHidden ? (
        // The collapsed rail — same shape as the right panel's own (below): 22px,
        // carries only the control that brings it back, grip centered vertically
        // (not pinned to the top — Alex found the old top-corner button easy to
        // miss). Points right, the direction the tree expands INTO.
        <div style={{
          position: 'relative', width: 22, flexShrink: 0, borderRight: '1px solid var(--th-border-subtle)',
        }}>
          <SplitterGrip onClick={toggleNavHidden} title="Show the tree" expanded={false} />
        </div>
      ) : (
        <>
          {/* Left nav — TreeNav (imported unchanged). Two cards, neither titled
              (Alex, 2026-08-20: "the filter & the tree must be in the same
              card and does not need a title" — the same reasoning extended to
              the switcher above it: both are NAVIGATION, not content, so
              there's nothing here for a chevron to collapse).
              ⚠ padding: 0 on BOTH card wrappers below, deliberately — ViewSwitcher
              and TreeNav each already carry their own internal spacing (tokens:
              --tg-filter-pad on the filter box, --tg-node-pad-x on every row).
              Adding CARD_STYLE's own 8px/12px on top of that DOUBLED the inset
              for the filter box specifically, since it's the one place that outer
              padding wasn't already cancelled by an edge-to-edge row background —
              "the filter box... same margin as the tree itself" (Alex, 2026-08-20).
              The outer edge spacing (aside's own padding, matched to <main>'s
              below) is what supplies the gap now, uniformly, not each card. */}
          {/* No overflowY here (dropped 2026-08-20) — TreeNav already scrolls its
              OWN row list internally (its `overflow-y:auto, flex:1` block), and
              the tree card above bounds that with its own `overflow:hidden`.
              A second scroll container on the aside was redundant, and its
              gutter was the actual cause of the tree card measuring narrower
              than the splitter-to-content gap on the right (Alex, 2026-08-20:
              "the card is a little bit wider... can it be reduced"). */}
          <aside style={{
            width: effectiveNavWidth, flexShrink: 0,
            display: 'flex', flexDirection: 'column', gap: PANE_GAP, padding: PANE_GAP,
          }}>
            {treeHeader && <div style={{ ...CARD_STYLE, padding: 0, overflow: 'hidden', marginBottom: 0 }}>{treeHeader}</div>}
            {/* flex:1 + minHeight:0 — bounds TreeNav's own `height:100%` to the
                space left AFTER treeHeader, instead of overflowing by
                treeHeader's height (NIGHT_SHIFT item 14, 2026-07-23). */}
            {!noTree && <div style={{ ...CARD_STYLE, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', marginBottom: 0, padding: 0 }}>
              <TreeNav
                nodes={treeNodes}
                selected={selectedNodeId}
                onSelect={onSelectNode}
                filterPlaceholder={filterPlaceholder}
                filterAside={treeFilterAside}
                title={treeTitle}
                sideIcons={treeSideIcons}
                loading={treeLoading}
                emptyMessage={treeEmptyMessage}
              />
            </div>}
          </aside>

          {/* Splitter — drag is unconditional (Alex, 2026-09-02: Customer/Proposal
              sit with nothing selected by default, which made drag read as
              permanently off there; the 2026-08-20 "nothing to resize against"
              gating no longer holds now every view here always has a nav rail
              worth resizing). The GRIP renders here regardless either way. */}
          <div
            onMouseDown={onSplitterMouseDown}
            style={{
              position: 'relative', width: 4, flexShrink: 0,
              cursor: 'col-resize', background: 'transparent',
            }}
            onMouseOver={e => { e.currentTarget.style.background = 'var(--tg-splitter-hover)'; }}
            onMouseOut={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            <SplitterGrip onClick={toggleNavHidden} title="Hide the tree" flip expanded />
          </div>
        </>
      ))}

      {/* Main panel */}
      <main style={mergedMainStyle}>
        {useSlotMode ? (
          pinnedHeader ? (
            <>
              {mainHeader}
              <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflowY: 'auto', overflowX: 'hidden' }}>
                {children}
                {spots.filter(Boolean).map((spot) => (
                  <Spot key={spot.key} spot={spot} storageKey={storageKey} />
                ))}
              </div>
            </>
          ) : (
            <>
              {mainHeader}
              {children}
              {spots.filter(Boolean).map((spot) => (
                <Spot key={spot.key} spot={spot} storageKey={storageKey} />
              ))}
            </>
          )
        ) : (
          <>
            {breadcrumbs && breadcrumbs.length > 0 && (
              <div style={{ marginBottom: 10, minHeight: 22 }}>
                <Breadcrumb crumbs={breadcrumbs} />
              </div>
            )}
            <DataGrid
              rows={gridRows}
              columns={gridColumns}
              emptyMessage={emptyState}
              storageKey={storageKey}
              {...gridHandlers}
            />
          </>
        )}
      </main>

      {/* Optional right-hand panel (e.g. ParameterPanel) — drag-resizable, mirrors
          the left nav splitter. The workspace owns the width; the panel fills it. */}
      {ownsRegions && (rightPanel || rightBottom) && rightHidden && (
        // The collapsed rail, grip centered vertically — points left, the
        // direction the panel expands INTO.
        <div style={{
          position: 'relative', width: 22, flexShrink: 0, borderLeft: '1px solid var(--th-border-subtle)',
        }}>
          <SplitterGrip onClick={toggleRightHidden} title="Show the side panel" flip expanded={false} />
        </div>
      )}

      {ownsRegions && (rightPanel || rightBottom) && !rightHidden && (
        <>
          {/* Splitter — DRAGGING only shows up when rightSelected (mirrors the left
              nav's own selectedNodeId gate). The GRIP always renders: hiding the
              panel has nothing to do with whether something's selected. */}
          <div
            onMouseDown={rightSelected ? onRightSplitterMouseDown : undefined}
            style={{
              position: 'relative', width: 4, flexShrink: 0,
              cursor: rightSelected ? 'col-resize' : 'default', background: 'transparent',
            }}
            onMouseOver={e => { if (rightSelected) e.currentTarget.style.background = 'var(--tg-splitter-hover)'; }}
            onMouseOut={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            <SplitterGrip onClick={toggleRightHidden} title="Hide the side panel" expanded />
          </div>
          <div style={{
            width: effectiveRightWidth, flexShrink: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column',
            padding: PANE_GAP, gap: PANE_GAP,
          }}>
            {/* The upper region is reserved even when EMPTY, so the chat below it stays
                pinned to the bottom of the column instead of floating up to the top on a
                screen that has no specific panel (Catalog, or Products before a selection).
                Alex, 2026-08-14: "can the assistant be always on the down part of the side
                panel even if the upper part is empty". An empty div costs nothing and it is
                one flex rule, where the alternative was every screen passing a spacer.
                NO shell-owned card here (2026-08-20, "can the right panel... be like the
                middle one" — the middle panel is a flat stack of independent cards, first/
                second/next, not one wrapper card; `rightPanel` now gets the same bare
                scroll region `mergedMainStyle` gives <main>, and owns its own Card stack —
                same discipline PlacementSidePanel and OnshapeDetailPanel now both follow. */}
            {(rightPanel || rightBottom) && (
              <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>{rightPanel}</div>
            )}
            {/* Shown whenever the chat is open: now that the upper region is always
                reserved, dragging is meaningful even on a screen with no specific
                panel — it is the chat's own height being set.
                ⚠ A SIBLING of the two cards, not nested inside the chat card
                (Alex, 2026-08-20: "the separation line is on the chat card" — it
                used to be the chat card's own first child, so once that card got
                a full border+white background, the splitter visually read as
                painted ON the card instead of floating independently between the
                two, the same way the left/main splitter sits in its own slot
                rather than inside either side. */}
            {bottomOpen && rightBottom && (
              <div
                onMouseDown={onBottomSplitterMouseDown}
                style={{ height: 4, flexShrink: 0, cursor: 'row-resize', background: 'transparent' }}
                onMouseOver={e => e.currentTarget.style.background = 'var(--tg-splitter-hover)'}
                onMouseOut={e => e.currentTarget.style.background = 'transparent'}
              />
            )}
            {rightBottom && (
              // Collapsed => height:auto, so the title bar is all that is reserved. A
              // fixed height here would leave a dead rectangle under a closed chat,
              // which is the opposite of "almost white and very subtle".
              <div style={{
                ...CARD_STYLE, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0,
                height: (!bottomNa && bottomOpen) ? bottomHeight : undefined,
                padding: 0, marginBottom: 0,
              }}>
                <div style={{ padding: '8px 10px 0', flexShrink: 0 }}>
                  <SectionTitle
                    collapsed={bottomNa ? true : !bottomOpen}
                    onToggle={bottomNa ? undefined : toggleBottom}
                    na={bottomNa}
                    naReason={bottomNa ? bottomNaReason : null}
                  >
                    {rightBottomTitle}
                  </SectionTitle>
                </div>
                {/* never mounted when not applicable — the assistant must not
                    fetch, or a refused request appears in the console of someone
                    who was never offered the feature. */}
                {!bottomNa && bottomOpen && (
                  <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    {rightBottom}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
    </NestedWorkspace.Provider>
  );
}
